import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import clsx from "clsx"
import _ from 'lodash'

import { Outlet, Link } from "../navigation";
import { DesignFamily, useDesigns } from "./DesignsProvider"
import EmptyState from "../components/EmptyState"
import { useRoles } from "../user/Auth";
import { TFunction } from "i18next";
import { designFamilyUrl, designRegistrationUrl } from "./utils";
import { useFilteredDesigns } from "../filter/FilteredDesigns";
import Image from "../components/Image"
import { design_family } from "../data"

export default function DesignPortfolio() {
    const {t} = useTranslation()

    const {isEditUser} = useRoles()

    const { isLoading } = useDesigns()
    const { designFamilies: families, registrationsByFamilyId } = useFilteredDesigns()

    return (
        <>
            {/* @ts-expect-error - Helmet types don't recognize title prop correctly */}
            <Helmet>
                <title>{t('designs')} | Patent Cockpit</title>
            </Helmet>
            <div className="portfolio-menu max-w-4xl sm:items-center">
                <h2 className="flex flex-row gap-1 md:gap-4 whitespace-nowrap shrink min-w-0 grow pr-4">
                    <Link to="/designs/portfolio" className="modern-h2">{t('portfolio')}</Link>
                </h2>
                {isEditUser && <div className="flex flex-col sm:flex-row gap-1 items-end sm:items-center">
                    <Link className={clsx(families?.length === 0 ? "btn-primary" : "btn-secondary", "py-0.5 text-sm")} to="add">
                        {t('add-design-family')}
                    </Link>
                </div>}
            </div>
            <div className="w-full overflow-auto grow">
                <div className="max-w-4xl p-4 pt-0 space-y-2">
                    {families?.length === 0 && !isLoading && <EmptyPortfolio />}
                    {_(families)
                        .sortBy(f => f.reference)
                        .reverse()
                        .map(family => <DesignFamilyCard key={family.reference} family={family} countryCount={registrationsByFamilyId[family.designFamilyId]?.length ?? 0} />)
                        .value()}
                </div>
            </div>
            <Outlet />
        </>
    )
}

function DesignFamilyCard({family, countryCount}: {family: DesignFamily, countryCount: number}) {
    const { registrationsByFamilyId } = useDesigns()

    const registrations = registrationsByFamilyId[family.designFamilyId] ?? []

    return (
        <div className="p-4 bg-white rounded-lg shadow">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_250px] max-lg:divide-y-2 lg:divide-x-2 divide-pcx-200">
                <div className="group max-lg:pb-2 md:pr-2 flex flex-col md:flex-row gap-4">
                    <div className="w-40 h-36 shrink-0">
                        <Image {...{
                            entity: design_family,
                            entityId: family.designFamilyId,
                            isEditable: false,
                            title: family.reference,
                            text: family.description
                        }} />
                    </div>
                    <Link to={designFamilyUrl(family)} className="block">
                        <h2 className="group-hover:text-pcx-800">{family.reference}</h2>
                        <p>{family.description}</p>
                    </Link>
                </div>
                <div className="max-lg:pt-2 lg:pl-2 flex flex-row flex-wrap content-start gap-1 text-sm">
                    {_(registrations)
                        .sortBy('countryCode')
                        .map((registration, ri) =>
                            <Link
                                key={registration.registrationId ?? ri}
                                to={designRegistrationUrl(registration)}
                                title={`${registration.reference}`}
                                className="ribbon hover:shadow hover:text-pcx-700 h-fit">
                                {registration.countryCode}
                            </Link>
                        ).value()}
                </div>
            </div>
        </div>
    )
}

const make_items = (t: TFunction) => [
    {
        title: t('add-design-family'),
        description: t('add-design-family-manually'),
        to: "add",
        icon: PencilSquareIcon,
        background: "bg-pcx-500",
    }
]

export function EmptyPortfolio() {
    const {t} = useTranslation()
    const {isEditUser} = useRoles()
    const items = isEditUser ? make_items(t) : []

    return <EmptyState {...{
        title: t("no-design-families"),
        text: t("add-design-families-desc"),
        items,
     }} />
}

export function EmptyFilteredPortfolio() {
    const {t} = useTranslation()
    const {isEditUser} = useRoles()
    const items = isEditUser ? make_items(t) : []

    return <EmptyState {...{
        title: t("no-filtered-design-families"),
        text: t("add-design-families-desc"),
        items,
     }} />
}
